import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr, money, fdt } from "../api";
import { useAuth } from "../context/AuthContext";
import { Star, CheckCircle2, Banknote } from "lucide-react";

const btnBlack = "bg-[#1C1B1A] text-white text-xs font-semibold px-4 py-2 rounded-full hover:bg-[#2C2B2A] transition-all disabled:opacity-40";

export function JobPaymentCard({ job, onChange }) {
  const { user } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/jobs/${job.id}/invoice`).then((r) => setInvoice(r.data.invoice)).catch(() => {});
  }, [job.id]);
  useEffect(() => { if (job.status === "delivered") load(); }, [job.status, load]);
  if (job.status !== "delivered" || !invoice) return null;

  const isCompany = user?.role === "company" || user?.role === "admin";
  const isCourier = user?.id === invoice.courier_id;

  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/invoice/${action}`);
      toast.success(action === "mark-paid" ? "Marked as paid" : "Receipt confirmed — settled");
      load();
      onChange?.();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <div className="cr-card !p-5" data-testid="job-payment-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-[#F2D06B] flex items-center justify-center"><Banknote className="w-5 h-5 text-[#1C1B1A]" /></span>
          <div>
            <p className="text-sm font-bold">Courier payment · {money(invoice.amount, invoice.currency)} <span className="text-[10px] font-normal text-[#A09D95]">ex VAT</span></p>
            <p className="text-xs text-[#78756E]">
              {invoice.status === "issued" && (isCompany ? "Waiting for your payment — pay the courier, then mark it paid." : "Invoice issued — waiting for the company to pay.")}
              {invoice.status === "paid" && (isCourier ? "The company marked this paid — confirm when the money arrives." : "Paid — waiting for the courier to confirm receipt.")}
              {invoice.status === "confirmed" && `Settled · confirmed ${fdt(invoice.confirmed_at)}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={invoice.status === "confirmed" ? "cr-pill-yellow" : "cr-pill-outline"} data-testid="invoice-status">
            {invoice.status === "issued" ? "To pay" : invoice.status === "paid" ? "Paid" : "Settled"}
          </span>
          {isCompany && invoice.status === "issued" && (
            <button data-testid="invoice-mark-paid" disabled={busy} onClick={() => act("mark-paid")} className={btnBlack}>Mark as paid</button>
          )}
          {isCourier && invoice.status === "paid" && (
            <button data-testid="invoice-confirm-received" disabled={busy} onClick={() => act("confirm-received")} className={btnBlack}>Confirm money received</button>
          )}
        </div>
      </div>
    </div>
  );
}

export function JobReviewCard({ job }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/jobs/${job.id}/reviews`).then((r) => setReviews(r.data.reviews)).catch(() => {});
  }, [job.id]);
  useEffect(() => { if (job.status === "delivered") load(); }, [job.status, load]);
  if (job.status !== "delivered") return null;

  const mine = reviews.find((r) => r.reviewer_id === user?.id);
  const isParty = user?.id === job.assigned_courier_id || user?.id === job.company_id || user?.role === "admin";

  const submit = async () => {
    if (!rating) return toast.error("Pick a star rating first");
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/review`, { rating, comment });
      toast.success("Review submitted");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <div className="cr-card !p-5" data-testid="job-review-card">
      <h2 className="text-sm font-bold mb-3">Reviews</h2>
      {reviews.map((r) => (
        <div key={r.id} className="py-2 border-b border-[#F6F3EA] last:border-0" data-testid={`review-${r.id}`}>
          <p className="text-xs font-semibold flex items-center gap-1.5">
            {r.reviewer_name}
            <span className="flex">{[1, 2, 3, 4, 5].map((s) => (
              <Star key={s} className={`w-3 h-3 ${s <= r.rating ? "text-[#F2D06B] fill-[#F2D06B]" : "text-[#E8E4D9]"}`} />
            ))}</span>
            <span className="text-[10px] text-[#A09D95] font-normal">{fdt(r.at)}</span>
          </p>
          {r.comment && <p className="text-xs text-[#78756E] mt-1">{r.comment}</p>}
        </div>
      ))}
      {reviews.length === 0 && <p className="text-xs text-[#A09D95]">No reviews yet.</p>}
      {isParty && !mine && (
        <div className="mt-4 pt-4 border-t border-[#F6F3EA]" data-testid="review-form">
          <p className="cr-label mb-2">Rate your {user?.id === job.assigned_courier_id ? "company" : "courier"}</p>
          <div className="flex items-center gap-1 mb-2.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} data-testid={`star-${s}`} onClick={() => setRating(s)}
                className="p-1 hover:scale-110 transition-transform">
                <Star className={`w-6 h-6 ${s <= rating ? "text-[#F2D06B] fill-[#F2D06B]" : "text-[#E8E4D9]"}`} />
              </button>
            ))}
          </div>
          <textarea data-testid="review-comment" rows={2} placeholder="Optional comment — punctuality, communication, handling…"
            value={comment} onChange={(e) => setComment(e.target.value)} className="cr-input w-full mb-2.5" />
          <button data-testid="review-submit" disabled={busy || !rating} onClick={submit} className={btnBlack}>
            <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Submit review
          </button>
        </div>
      )}
    </div>
  );
}
